import { useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useTeamInfo, useTeamMembers, renameTeam, setTeamLogo, deleteTeam } from '../tareas/api';
import { uploadTeamLogo } from '../tareas/storage';

export default function TeamSettings() {
  const { profile, reloadProfile, teams } = useAuth();
  const { team, loading, refetch } = useTeamInfo();
  const { members } = useTeamMembers();

  const [nombre, setNombre] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);

  if (loading || !team) {
    return (
      <section style={styles.card}>
        <h2 style={styles.cardTitle}>Equipo</h2>
        <p style={styles.muted}>{loading ? 'Cargando…' : 'No se pudo cargar el equipo.'}</p>
      </section>
    );
  }

  // Cualquier admin (owner o admin) puede gestionar el equipo.
  const myRole = members.find((m) => m.id === profile?.id)?.role;
  const canManage = myRole === 'owner' || myRole === 'admin';
  // Solo el creador/propietario puede eliminar, y nunca su único espacio.
  const isOwner = team.owner_id === profile?.id;
  const canDelete = isOwner && teams.length > 1;

  const doDelete = async () => {
    setDeleting(true); setError('');
    try {
      await deleteTeam(team.id);
      // Recarga membresías: AuthProvider re-resuelve el espacio activo (el
      // borrado ya no está en la lista) o muestra el onboarding si era el único.
      await reloadProfile();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo eliminar el espacio');
      setDeleting(false);
      setConfirmDelete(false);
      setDeleteText('');
    }
  };

  const saveName = async () => {
    setBusy(true); setError('');
    try { await renameTeam(team.id, nombre); setEditingName(false); await refetch(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Error'); }
    finally { setBusy(false); }
  };

  const onPickLogo = async (file: File | undefined) => {
    if (logoInputRef.current) logoInputRef.current.value = '';
    if (!file) return;
    setUploadingLogo(true); setError('');
    try {
      const url = await uploadTeamLogo(file, team.id);
      await setTeamLogo(team.id, url);
      await refetch();
      await reloadProfile(); // refresca la marca del sidebar
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo subir el logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    setUploadingLogo(true); setError('');
    try {
      await setTeamLogo(team.id, null);
      await refetch();
      await reloadProfile();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo quitar el logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <>
    <section style={styles.card}>
      <h2 style={styles.cardTitle}>Equipo</h2>

      {/* Nombre del equipo */}
      <div style={styles.row}>
        <span style={styles.label}>Nombre</span>
        {editingName && canManage ? (
          <>
            <input value={nombre} onChange={(e) => setNombre(e.target.value)} maxLength={80} style={styles.input} />
            <button onClick={saveName} disabled={busy || !nombre.trim()} style={styles.btnPrimary}>Guardar</button>
            <button onClick={() => setEditingName(false)} style={styles.btnGhost}>Cancelar</button>
          </>
        ) : (
          <>
            <span style={styles.teamName}>{team.nombre}</span>
            {canManage && (
              <button onClick={() => { setNombre(team.nombre); setEditingName(true); }} style={styles.btnGhost}>
                Renombrar
              </button>
            )}
          </>
        )}
      </div>

      {/* Logo del equipo */}
      <div style={{ ...styles.row, marginTop: 14 }}>
        <span style={styles.label}>Logo</span>
        <span style={styles.logoPreview}>
          {team.logo_url ? (
            <img src={team.logo_url} alt="" style={styles.logoImg} />
          ) : (
            <span style={styles.logoMonogram}>{team.nombre.charAt(0).toUpperCase()}</span>
          )}
        </span>
        {canManage && (
          <>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
              style={{ display: 'none' }}
              onChange={(e) => onPickLogo(e.target.files?.[0])}
            />
            <button
              onClick={() => logoInputRef.current?.click()}
              disabled={uploadingLogo}
              style={styles.btnPrimary}
            >
              {uploadingLogo ? 'Subiendo…' : team.logo_url ? 'Cambiar' : 'Subir logo'}
            </button>
            {team.logo_url && (
              <button onClick={removeLogo} disabled={uploadingLogo} style={styles.btnGhost}>
                Quitar
              </button>
            )}
          </>
        )}
      </div>

      {error && <p style={styles.error}>{error}</p>}
    </section>

    {/* Zona de peligro: eliminar el espacio. Solo el propietario, y nunca
        si es el único espacio del que es miembro. */}
    {canDelete && (
      <section style={styles.dangerCard}>
        <h2 style={styles.dangerTitle}>Eliminar espacio de trabajo</h2>
        <p style={styles.dangerText}>
          Se borrarán de forma permanente todas las tareas, recordatorios, miembros e
          invitaciones de <strong>{team.nombre}</strong>. Esta acción no se puede deshacer.
        </p>

        {!confirmDelete ? (
          <button onClick={() => setConfirmDelete(true)} style={styles.btnDanger}>
            Eliminar espacio
          </button>
        ) : (
          <div>
            <p style={styles.dangerText}>
              Escribe <strong>{team.nombre}</strong> para confirmar:
            </p>
            <div style={styles.row}>
              <input
                value={deleteText}
                onChange={(e) => setDeleteText(e.target.value)}
                placeholder={team.nombre}
                style={styles.input}
                autoFocus
              />
              <button
                onClick={doDelete}
                disabled={deleting || deleteText.trim() !== team.nombre.trim()}
                style={styles.btnDanger}
              >
                {deleting ? 'Eliminando…' : 'Eliminar definitivamente'}
              </button>
              <button
                onClick={() => { setConfirmDelete(false); setDeleteText(''); }}
                disabled={deleting}
                style={styles.btnGhost}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </section>
    )}
    </>
  );
}

const styles: Record<string, CSSProperties> = {
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 22, marginBottom: 18 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 16px' },
  row: { display: 'flex', alignItems: 'center', gap: 10 },
  label: { width: 70, fontSize: 13, fontWeight: 600, color: '#64748b' },
  teamName: { flex: 1, fontSize: 15, fontWeight: 600, color: '#0f172a' },
  logoPreview: { flex: 1, display: 'flex', alignItems: 'center' },
  logoImg: { width: 44, height: 44, borderRadius: 10, objectFit: 'cover', border: '1px solid #e2e8f0' },
  logoMonogram: {
    width: 44, height: 44, borderRadius: 10, background: '#eef2ff', color: '#4338ca',
    fontWeight: 800, fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  input: { border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', background: '#fff' },
  btnPrimary: { background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnGhost: { background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#475569', fontSize: 13, fontWeight: 600 },
  muted: { color: '#94a3b8', fontSize: 14, margin: 0 },
  error: { color: '#dc2626', fontSize: 13, marginTop: 12, marginBottom: 0 },
  dangerCard: { background: '#fff', border: '1px solid #fecaca', borderRadius: 12, padding: 22, marginBottom: 18 },
  dangerTitle: { fontSize: 15, fontWeight: 700, color: '#b91c1c', margin: '0 0 8px' },
  dangerText: { fontSize: 13, color: '#64748b', margin: '0 0 14px', lineHeight: 1.5 },
  btnDanger: { background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
};
